// Multiplayer worldbuilding: the six Ink Houses, the rotating Frontiers and the weekly Atlas War.
//
// Canon (short form — the full bible lives in docs/MULTIPLAYER-WORLD.md):
//   The Ink Atlas was the one map that held every realm. When the Void tore it, six Houses — one per realm —
//   each took a shard and swore to redraw the world in their own colours. Online arenas are the "Frontier":
//   the torn edge where the shards overlap. Every 20 minutes the Frontier drifts to another realm and that
//   realm's law bends the rules. Every week the Houses tally the land their inkers drew; the winning House
//   holds the Atlas Crown until the next tally.
//
// Everything here is pure data + pure functions so the server (authority), the client (UI) and tests agree.
// Adding a House or a Frontier = add an entry (+ i18n keys house.<id>.* / frontier.<id>.*). No code changes.

import { REALMS } from './content';

// ---------------------------------------------------------------- Ink Houses (factions)

export interface HouseDef {
  /** Stable id stored in the database. Never rename. */
  id: string;
  /** Realm this House was born from (colours + art). */
  realm: string;
  emblem: string;
  color: string;
}

export const HOUSES: HouseDef[] = [
  { id: 'grove', realm: 'verdant', emblem: '🌿', color: '#3FBF6B' },
  { id: 'cinder', realm: 'ember', emblem: '🔥', color: '#FF7A3D' },
  { id: 'tide', realm: 'tidal', emblem: '🌊', color: '#2E9BFF' },
  { id: 'rime', realm: 'frost', emblem: '❄️', color: '#8FD8FF' },
  { id: 'pulse', realm: 'neon', emblem: '⚡', color: '#FF3DC8' },
  { id: 'star', realm: 'astral', emblem: '✨', color: '#9B7BFF' },
];

export const HOUSE_IDS = HOUSES.map((h) => h.id);

export function houseById(id: string | null | undefined): HouseDef | undefined {
  return id ? HOUSES.find((h) => h.id === id) : undefined;
}

// ---------------------------------------------------------------- Frontiers (rotating arena laws)

/**
 * Server-side rule tweaks. Only parameters the client never predicts (spawn tables, rush timing, scoring,
 * cooldowns) — movement stays identical so own-head prediction is unaffected.
 */
export interface FrontierRules {
  /** Multiplies the room's power-up cap. */
  powerupMult: number;
  /** Arena power-up table (kinds repeated = weight). */
  powerKinds: number[];
  /** Seconds between Land Rush zones / how long one lasts. */
  rushInterval: number;
  rushDuration: number;
  /** Extra score per rush cell, as a multiple of the base (2 = "pays triple"). */
  rushBonus: number;
  /** Treasure power-ups dropped in each rush zone. */
  rushTreasure: number;
  /** Seconds a capture combo stays open. */
  comboWindow: number;
  /** Score for ending the leader (bounty) / anyone else. */
  bountyScore: number;
  killScore: number;
  /** Dash cooldown in seconds. */
  dashCooldown: number;
  /** Ink Royale: seconds per Void ring. */
  voidRingSeconds: number;
}

/** Baseline = the classic rules (matches the engine constants). */
export const BASE_FRONTIER: FrontierRules = {
  powerupMult: 1,
  powerKinds: [1, 1, 2, 2, 3, 5],
  rushInterval: 40,
  rushDuration: 20,
  rushBonus: 2,
  rushTreasure: 2,
  comboWindow: 4.5,
  bountyScore: 1500,
  killScore: 500,
  dashCooldown: 7,
  voidRingSeconds: 1.4,
};

export interface FrontierDef {
  /** Same id as the realm it belongs to. i18n: frontier.<id>.name / frontier.<id>.law */
  id: string;
  icon: string;
  /** House that gets the home-ground bonus (war points ×1.25) while this Frontier is active. */
  house: string;
  rules: FrontierRules;
}

const f = (p: Partial<FrontierRules>): FrontierRules => ({ ...BASE_FRONTIER, ...p });

export const FRONTIERS: FrontierDef[] = [
  // Overgrowth: the land itself helps — lots of Bloom seeds.
  { id: 'verdant', icon: '🌱', house: 'grove', rules: f({ powerupMult: 1.7, powerKinds: [1, 2, 3, 3, 3, 5] }) },
  // Wildfire: golden zones flare up twice as often and pay more.
  { id: 'ember', icon: '🔥', house: 'cinder', rules: f({ rushInterval: 22, rushBonus: 3, powerKinds: [1, 2, 3, 5, 5, 5] }) },
  // High Tide: combos stay open much longer.
  { id: 'tidal', icon: '🌊', house: 'tide', rules: f({ comboWindow: 7.5 }) },
  // Bounty Blizzard: hunting the leader pays double.
  { id: 'frost', icon: '❄️', house: 'rime', rules: f({ bountyScore: 3000, killScore: 750 }) },
  // Overclock: dash recharges fast, dash power-ups everywhere.
  { id: 'neon', icon: '⚡', house: 'pulse', rules: f({ dashCooldown: 4, powerKinds: [1, 1, 1, 2, 3, 5] }) },
  // Starfall: long-lasting golden zones raining treasure; the Void closes slower.
  { id: 'astral', icon: '🌠', house: 'star', rules: f({ rushDuration: 30, rushTreasure: 5, voidRingSeconds: 1.8 }) },
];

/** How long one Frontier holds before drifting to the next realm. */
export const FRONTIER_PERIOD_MS = 20 * 60 * 1000;

export function frontierById(id: string | null | undefined): FrontierDef | undefined {
  return id ? FRONTIERS.find((x) => x.id === id) : undefined;
}

/**
 * The global Frontier schedule. Everyone on Earth sees the same Frontier at the same time, so it can be
 * announced in menus ("Wildfire — 12 min left") and planned around. Order is shuffled per day so the same
 * hour of day doesn't always get the same law.
 */
export function frontierAt(ms: number): { def: FrontierDef; index: number; startsAt: number; endsAt: number } {
  const slot = Math.floor(ms / FRONTIER_PERIOD_MS);
  const slotsPerDay = (24 * 3600 * 1000) / FRONTIER_PERIOD_MS;
  const day = Math.floor(slot / slotsPerDay);
  const offset = (day * 7 + 3) % FRONTIERS.length;
  const index = (slot + offset) % FRONTIERS.length;
  return { def: FRONTIERS[index], index, startsAt: slot * FRONTIER_PERIOD_MS, endsAt: (slot + 1) * FRONTIER_PERIOD_MS };
}

/** Realm colours for a Frontier (renderer tint). */
export function frontierRealm(id: string) {
  return REALMS.find((r) => r.id === id) ?? REALMS[0];
}

// ---------------------------------------------------------------- Atlas War (weekly faction season)

const WEEK_MS = 7 * 24 * 3600 * 1000;
/** 1970-01-05 was a Monday: weeks run Monday 00:00 UTC → Monday 00:00 UTC. */
const MONDAY_EPOCH = 4 * 24 * 3600 * 1000;

export function warWeek(ms: number = Date.now()): number {
  return Math.floor((ms - MONDAY_EPOCH) / WEEK_MS);
}

export function warWeekEndsAt(week: number): number {
  return MONDAY_EPOCH + (week + 1) * WEEK_MS;
}

export interface WarContribution {
  mode: string;
  maxPct: number;
  kills: number;
  won?: boolean;
  /** Frontier active when the match ended. */
  frontier?: string;
}

/** Hard cap per match so no single life can swing the war. */
export const WAR_MATCH_CAP = 400;

/** War points a single life / round is worth to the player's House. */
export function warPoints(c: WarContribution, house: string): number {
  const pct = Math.max(0, Math.min(100, c.maxPct));
  let pts = Math.round(pct * 4) + Math.min(20, Math.max(0, c.kills | 0)) * 6 + (c.won ? 60 : 0);
  if (c.mode === 'royale' && c.won) pts += 40;
  // Home ground: fighting on your House's own Frontier counts 25% more.
  const fr = frontierById(c.frontier);
  if (fr && fr.house === house) pts = Math.round(pts * 1.25);
  return Math.max(0, Math.min(WAR_MATCH_CAP, pts));
}

/** Coins paid when a member claims last week's result. */
export const WAR_REWARD = { winner: 300, member: 80, minPoints: 50 } as const;

/** Ranks houses by points (stable by id order on ties). */
export function rankHouses(points: Record<string, number>): { id: string; points: number; rank: number }[] {
  return HOUSES.map((h, i) => ({ id: h.id, points: points[h.id] ?? 0, i }))
    .sort((a, b) => b.points - a.points || a.i - b.i)
    .map((r, k) => ({ id: r.id, points: r.points, rank: k + 1 }));
}
